import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ScoreData {
  totalPoints: number;
  level: number;
  streak: number;
  rank: string;
  rankProgress: number;
  history: Array<{
    date: string;
    points: number;
    activity: string;
  }>;
  achievements: Array<{
    id: string;
    name: string;
    description: string;
    icon: string;
    unlockedAt?: string;
  }>;
}

interface ScoreStore {
  score: ScoreData;
  addPoints: (points: number, activity: string) => void;
  incrementStreak: () => void;
  resetStreak: () => void;
  unlockAchievement: (achievementId: string) => void;
  setScore: (score: ScoreData) => void;
  clearScore: () => void;
}

const initialScore: ScoreData = {
  totalPoints: 0,
  level: 1,
  streak: 0,
  rank: 'Seedling',
  rankProgress: 0,
  history: [],
  achievements: [],
};

export const useScoreStore = create<ScoreStore>()(
  persist(
    (set) => ({
      score: initialScore,

      addPoints: (points, activity) =>
        set((state) => {
          const newTotal = state.score.totalPoints + points;
          const newLevel = Math.floor(newTotal / 1000) + 1;
          const rankProgress = (newTotal % 1000) / 1000;

          return {
            score: {
              ...state.score,
              totalPoints: newTotal,
              level: newLevel,
              rankProgress,
              history: [
                {
                  date: new Date().toISOString(),
                  points,
                  activity,
                },
                ...state.score.history,
              ].slice(0, 50), // Keep last 50 entries
            },
          };
        }),

      incrementStreak: () =>
        set((state) => ({
          score: {
            ...state.score,
            streak: state.score.streak + 1,
          },
        })),

      resetStreak: () =>
        set((state) => ({
          score: {
            ...state.score,
            streak: 0,
          },
        })),

      unlockAchievement: (achievementId) =>
        set((state) => ({
          score: {
            ...state.score,
            achievements: state.score.achievements.map((achievement) =>
              achievement.id === achievementId
                ? { ...achievement, unlockedAt: new Date().toISOString() }
                : achievement
            ),
          },
        })),

      setScore: (score) => set({ score }),

      clearScore: () => set({ score: initialScore }),
    }),
    {
      name: 'score-storage',
    }
  )
);
